import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { isEqual, isUndefined } from 'lodash';
import React, { Fragment, useEffect, useState } from 'react';
import { notAllowedNumberCharacters } from '../../constants/globals';
import ErrorLabel from './ErrorLabel';

const BasicTextField = (props) => {
  const { inputProps } = props || {},
    {
      error,
      type,
      id,
      name,
      icon,
      placeholder,
      value,
      helperText,
      focused,
      autoFocus,
      disabled,
      required,
      autoComplete,
      multiline,
      rows,
      onlyPositive,
      className,
      variant,
      margin,
      passwordVisibility,
      onClick,
      onChange,
      onBlur,
      errorsHandler,
      inputPlaceholder,
      readOnly,
      color,
      clearable,
    } = inputProps || {};

  const [showPassword, setShowPassword] = useState(!!passwordVisibility);
  const inputValue = value === null ? '' : value;
  const [valueState, setValueState] = useState(inputValue);

  const isTypePassword = type === 'password';
  const inputType = isTypePassword && showPassword ? 'text' : type;
  const errorMessage = typeof error !== 'string' ? error?.[name] : error;
  const hasError = !isUndefined(errorMessage);

  const size = inputProps?.size || 'small';
  const fieldPlaceholder = icon ? (
    <span className="d-flex align-items-center">
      {icon}
      {placeholder}
    </span>
  ) : (
    placeholder
  );

  useEffect(() => {
    !isEqual(value, valueState) && setValueState(inputValue);
  }, [value, valueState, inputValue]);

  const onClickShowPasswordHandler = () => {
    setShowPassword(!showPassword);
  };

  const onMouseDownPasswordHandler = (event) => {
    event.preventDefault();
  };

  const errorRemovalHandler = () => {
    if (errorsHandler)
      errorsHandler((prevError) => {
        delete prevError[name];
        return prevError;
      });
  };

  const inputEndAdornment =
    (isTypePassword && (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={onClickShowPasswordHandler}
          onMouseDown={onMouseDownPasswordHandler}
          edge="end"
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    )) ||
    (!!clearable && valueState && !readOnly && !disabled && (
      <InputAdornment position="end" size="small">
        <IconButton
          onClick={() => {
            onChange?.({ target: { name, value: '' } });
            setValueState('');
          }}
          onMouseDown={onMouseDownPasswordHandler}
          edge="end"
        >
          {<ClearRoundedIcon />}
        </IconButton>
      </InputAdornment>
    ));

  const conditionForAllowedInput = (event) =>
    event.nativeEvent.inputType === 'insertText' &&
    notAllowedNumberCharacters.find((e) => e === event.nativeEvent.data);

  return (
    <TextField
      id={`input-field-${id}`}
      name={name}
      label={fieldPlaceholder}
      type={inputType}
      value={valueState}
      helperText={helperText}
      error={hasError}
      focused={!!focused}
      autoFocus={!!autoFocus}
      disabled={!!disabled}
      required={required}
      autoComplete={autoComplete || ''}
      multiline={multiline}
      rows={rows}
      size={size}
      fullWidth
      className={className}
      variant={variant || 'outlined'}
      margin={margin || 'normal'}
      color={color || 'primary'}
      onClick={onClick}
      onKeyDown={(evt) =>
        evt.key === 'e' && onlyPositive && evt.preventDefault()
      }
      onChange={(event) => {
        if (onlyPositive) {
          !conditionForAllowedInput?.(event) && onChange?.(event);
        } else {
          setValueState(event.target?.value);
          onChange?.(event);
        }
        hasError && errorRemovalHandler();
      }}
      onBlur={onBlur}
      InputLabelProps={{
        shrink: true,
      }}
      InputProps={{
        inputProps: {
          min: onlyPositive && 0,
        },
        endAdornment: inputEndAdornment,
        placeholder: inputPlaceholder,
        readOnly: !!readOnly,
      }}
    />
  );
};

const ToolTipTextField = (props) => {
  const { tootipProps } = props || {};
  return (
    <Tooltip
      title={tootipProps.tooltipText}
      placement={tootipProps?.tooltipPlacement || 'bottom'}
      arrow={true}
    >
      <div>
        <BasicTextField inputProps={tootipProps} />
      </div>
    </Tooltip>
  );
};

const InputField = (props) => {
  const {
    error,
    dontShowErrLabel,
    tooltipText,
    name,
    hidden = false,
  } = props || {};
  const errorMessage = typeof error !== 'string' ? error?.[name] : error;

  if (hidden) {
    return <Fragment />;
  }

  return (
    <Fragment>
      {tooltipText ? (
        <ToolTipTextField tootipProps={props} />
      ) : (
        <BasicTextField inputProps={props} />
      )}
      {!dontShowErrLabel && <ErrorLabel error={errorMessage} />}
    </Fragment>
  );
};

export default InputField;
